import React, { useState, useCallback } from 'react';
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButton,
  IonButtons,
  IonBackButton,
  IonSpinner,
  useIonViewWillLeave,
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { apiCall } from '@/util/api';
import { useAppConfig } from '@/modules/config';
import SnowballMenu from '@/modules/user/SnowballMenu';
import { TopicLogic } from './logic';
import TopicTypeBadge from './read/TopicTypeBadge';
import { enabledTopicTypes } from './topicTypeRegistry';
import MarkdownBody from './read/MarkdownBody';
import WhereWhenBlock from './read/WhereWhenBlock';
import BylineDatelineBlock from '@/modules/topic_garden/news/BylineDatelineBlock';
import ChangesLinkBlock from './read/ChangesLinkBlock';
import CompositeEngagementBlock from './read/CompositeEngagementBlock';
import ListingManagersBlock from './read/ListingManagersBlock';
import RegistrationBlock from './read/RegistrationBlock';
import ShareDock from './read/ShareDock';
import TopicOverflowMenu from './read/TopicOverflowMenu';
import DeleteTopicSheet from './read/DeleteTopicSheet';
import NotFoundCard from './read/NotFoundCard';
import PrivateInvitePlaceholder from './read/PrivateInvitePlaceholder';
import { useTopicReadInterstitials } from './read/useTopicReadInterstitials';
import {
  pageBackground,
  heroRowStyle,
  heroImageColStyle,
  heroImageStyle,
  heroInfoColStyle,
  heroTitleRowStyle,
  heroTitleStyle,
  pageHeaderTitleStyle,
  readHeroStyle,
} from './read/styles';
import { useTopicUpsert } from './upsert/useTopicUpsert';
import {
  getPageHeader,
  centeredHeaderStyle,
  pageHeaderRowStyle,
} from './upsert/constants';
import TitleAndTypeBlock from './upsert/TitleAndTypeBlock';
import TopicBasicsBlock from './upsert/TopicBasicsBlock';
import EditableWhereWhenBlock from './upsert/EditableWhereWhenBlock';
import VisibilityLimitsBlock from './upsert/VisibilityLimitsBlock';
import CreateFooter from './upsert/CreateFooter';
import UpdateToolbar from './upsert/UpdateToolbar';
import PageRefresher from '@/components/PageRefresher';
import CustomizeTemplatesModal from './CustomizeTemplatesModal';
import { useTopicState } from './TopicStateContext';

export type TopicViewMode = 'create' | 'view' | 'update';

interface TopicViewProps {
  initialMode: TopicViewMode;
  topicId?: string;
}

export const TopicView: React.FC<TopicViewProps> = ({
  initialMode,
  topicId,
}) => {
  const history = useHistory();
  const { features } = useAppConfig();
  const {
    // form fields
    topicTitle,
    setTopicTitle,
    topicBody,
    setTopicBody,
    topicImageData,
    setTopicImageData,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    timezone,
    setTimezone,
    timezones,
    endBeforeStart,
    hasChanges,
    introLimitCountEnabled,
    setIntroLimitCountEnabled,
    introLimitCount,
    setIntroLimitCount,
    onlyManagersCanShare,
    setOnlyManagersCanShare,
    byInviteOnly,
    setByInviteOnly,
    type,
    setType,
    customTemplates,
    setCustomTemplates,
    impressionGoal,
    setImpressionGoal,
    // controller
    mode,
    topic,
    registration,
    loading,
    deleting,
    showDeleteSheet,
    setShowDeleteSheet,
    fetchTopicInfo,
    handleEnterUpdate,
    handleCancelUpdate,
    handleShareTopic,
    handleShareUpdates,
    handleDeleteTopic,
    // upsert
    saving,
    canCreateTopics,
    globalIntroMax,
    handleSelectTopicImage,
    createTopic,
    updateTopic,
  } = useTopicUpsert({ initialMode, topicId });

  const { state: topicState, dispatch: dispatchTopicState } = useTopicState();
  const topicMutations = (topicId && topicState.mutationsById?.[topicId]) || [];

  // The welcome / private-invite / phone-verification experience, shared with
  // the component-driven read view. Owns the receivedUpdate signal, the
  // by-invitation-only detection + masked placeholder, and in-place verification.
  const {
    invitePreviewData,
    privateInviteResolved,
    goToProfileWizardForInvite,
    interstitials,
  } = useTopicReadInterstitials({
    topicId,
    topic,
    loading,
    initialMode,
    onTopicBecameReadable: fetchTopicInfo,
  });

  const [showTemplatesModal, setShowTemplatesModal] = useState(false);

  useIonViewWillLeave(() => {
    const activeElement = document.activeElement;
    if (!(activeElement instanceof HTMLElement)) return;
    const shadowActive = activeElement.shadowRoot?.activeElement;
    if (shadowActive instanceof HTMLElement) shadowActive.blur();
    activeElement.blur();
  });

  // Accept the validated YAML from the editor: stash it into the form, where it
  // persists with the page's Create/Update button. The store keeps the saved
  // topic, so the form's edit is what enables Update.
  const handleDoneTemplates = useCallback(
    (yaml: string) => {
      setCustomTemplates(yaml);
      setShowTemplatesModal(false);
    },
    [setCustomTemplates]
  );

  // Revert clears the custom templates in memory and, for an already-saved
  // topic, immediately in the DB — building the PUT from the saved topic so
  // unsaved edits to other fields aren't persisted.
  const handleRevertTemplates = useCallback(async () => {
    setCustomTemplates(undefined);
    if (topicId && topic) {
      dispatchTopicState({
        type: 'UPDATE_CUSTOM_TEMPLATES',
        topicId,
        customTemplates: undefined,
      });
      try {
        const encodedTopicId = encodeURIComponent(topicId);
        const response = await apiCall(`/api/topics/${encodedTopicId}`, {
          method: 'PUT',
          body: JSON.stringify({
            title: topic.title,
            body: topic.body,
            startTime: topic.startTime,
            endTime: topic.endTime,
            timezone: topic.timezone,
            type: topic.type,
            introCountLimit: topic.introCountLimit ?? null,
            onlyManagersCanShare: topic.onlyManagersCanShare ?? false,
            byInviteOnly: topic.byInviteOnly ?? false,
            customTemplates: undefined,
          }),
        });
        if (response.ok) {
          const data = await response.json();
          dispatchTopicState({
            type: 'UPSERT_TOPIC',
            topic: TopicLogic.fromJSON(data),
          });
        }
      } catch {
        // Best-effort: the in-memory clear already happened and the page's
        // Update button will persist the cleared value on next save.
      }
    }
    setShowTemplatesModal(false);
  }, [topicId, topic, setCustomTemplates, dispatchTopicState]);

  const isCreateMode = mode === 'create';
  const isViewMode = mode === 'view';
  const isUpdateMode = mode === 'update';
  const isEditable = isCreateMode || isUpdateMode;

  const pageHeader = getPageHeader(mode, topic);

  const renderDateTimeSection = () => {
    if (isEditable) {
      return (
        <EditableWhereWhenBlock
          startTime={startTime}
          setStartTime={setStartTime}
          endTime={endTime}
          setEndTime={setEndTime}
          timezone={timezone}
          setTimezone={setTimezone}
          timezones={timezones}
          endBeforeStart={endBeforeStart}
        />
      );
    }
    if (!topic) return null;
    // A news story shows its byline and dateline, and has no end date.
    return topic.type === 'news' ? (
      <BylineDatelineBlock topic={topic} />
    ) : (
      <WhereWhenBlock topic={topic} />
    );
  };

  // Links to the change-history subpage when the manager has made edits this
  // session. The heading always shows; the "share updates" subtitle only shows
  // to users who can share the topic (#418).
  const renderChangesLinkSection = () => {
    if (!isViewMode || !topicId) return null;
    return (
      <ChangesLinkBlock
        topicId={topicId}
        hasMutations={topicMutations.length > 0}
      />
    );
  };

  const renderEngagementSection = () => {
    if (!isViewMode || !topicId) return null;
    return (
      <CompositeEngagementBlock
        topicId={topicId}
        topicTitle={topic?.title}
        onMetricsOpen={path => history.push(path)}
        topic={topic ?? undefined}
        showLeaderboard={type === 'news'}
      />
    );
  };

  // Bottom-of-page card listing the topic's listing managers so a viewer can
  // contact them. Only shown in view mode and only when the detail response
  // actually carried manager emails.
  const renderListingManagersSection = () => {
    if (!isViewMode) return null;
    return <ListingManagersBlock managerEmails={topic?.managerEmails ?? []} />;
  };

  const renderTypeSection = () =>
    topic ? <TopicTypeBadge topic={topic} /> : null;

  // Edit mode: title (with the legacy type chips), image and body as one
  // untitled card — the fields a topic cannot do without, read as one group.
  const renderBasicsSection = () => {
    if (!isEditable) return null;
    return (
      <TopicBasicsBlock
        titleBlock={
          <TitleAndTypeBlock
            topicTitle={topicTitle}
            setTopicTitle={setTopicTitle}
            type={type}
            setType={setType}
            enabledTypes={enabledTopicTypes(features)}
          />
        }
        imageData={topicImageData}
        onSelectFile={handleSelectTopicImage}
        onClearImage={() => setTopicImageData(null)}
        topicBody={topicBody}
        setTopicBody={setTopicBody}
      />
    );
  };

  const renderBodySection = () => {
    if (isEditable || !topic || !topic.body) {
      return null;
    }
    return <MarkdownBody body={topic.body} />;
  };

  const renderViewTopicsSection = () => {
    if (!topicId || !isViewMode) {
      return null;
    }
    return (
      <RegistrationBlock
        topicId={topicId}
        registration={registration}
        registrationEnabled={!!features.topicRegistrationEnabled}
        onGuardSuccess={fetchTopicInfo}
      />
    );
  };

  const renderShareDock = () => {
    if (!topicId || !isViewMode || !topic) return null;
    return (
      <ShareDock
        topic={topic}
        topicId={topicId}
        features={features}
        topicMutations={topicMutations}
        onShareTopic={handleShareTopic}
        onShareUpdates={handleShareUpdates}
        onGuardSuccess={fetchTopicInfo}
      />
    );
  };

  const renderResharingLimitsSection = () => {
    if (!isEditable) {
      return null;
    }
    return (
      <VisibilityLimitsBlock
        byInviteOnly={byInviteOnly}
        setByInviteOnly={setByInviteOnly}
        introLimitCountEnabled={introLimitCountEnabled}
        setIntroLimitCountEnabled={setIntroLimitCountEnabled}
        introLimitCount={introLimitCount}
        setIntroLimitCount={setIntroLimitCount}
        globalIntroMax={globalIntroMax}
        impressionGoal={impressionGoal}
        setImpressionGoal={setImpressionGoal}
        onlyManagersCanShare={onlyManagersCanShare}
        setOnlyManagersCanShare={setOnlyManagersCanShare}
      />
    );
  };

  const renderCreateFooter = () => {
    if (!isCreateMode) {
      return null;
    }
    return (
      <CreateFooter
        endBeforeStart={endBeforeStart}
        saving={saving}
        topicTitle={topicTitle}
        canCreateTopics={canCreateTopics}
        onCreate={createTopic}
      />
    );
  };

  // Advanced affordance for topic managers: customize the message templates
  // for this topic. Shown while creating or editing.
  const renderCustomizeTemplatesLink = () => {
    if (!isCreateMode && !isUpdateMode) {
      return null;
    }
    return (
      <div style={{ marginTop: '8px', textAlign: 'center' }}>
        <IonButton
          fill="clear"
          size="small"
          onClick={() => setShowTemplatesModal(true)}
          data-testid="customize-templates-link"
        >
          {customTemplates
            ? 'Edit custom msg templates'
            : 'Customize message templates'}
        </IonButton>
      </div>
    );
  };

  const renderToolbar = () => {
    if (isUpdateMode) {
      return (
        <UpdateToolbar
          topicId={topicId}
          saving={saving}
          topicTitle={topicTitle}
          endBeforeStart={endBeforeStart}
          hasChanges={hasChanges}
          onCancel={handleCancelUpdate}
          onUpdate={updateTopic}
        />
      );
    }

    return (
      <IonToolbar>
        {(isCreateMode || !loading) && (
          <IonButtons slot="start">
            <IonBackButton defaultHref="/topics" />
          </IonButtons>
        )}
        {isCreateMode ? (
          <IonTitle>Create Topic</IonTitle>
        ) : !loading &&
          !topic &&
          !invitePreviewData &&
          privateInviteResolved ? (
          <IonTitle>Topic Not Found</IonTitle>
        ) : (
          <IonTitle className="ion-hide-accessible">Topic Details</IonTitle>
        )}
        <IonButtons slot="end">
          <SnowballMenu />
        </IonButtons>
      </IonToolbar>
    );
  };

  // Edit/Delete overflow menu for the topic manager, anchored to the top-right
  // of the title (its button is absolutely positioned, so the container needs a
  // positioned ancestor).
  const renderTopicsMenu = () => {
    if (!isViewMode || !topicId || !topic) {
      return null;
    }
    return (
      <TopicOverflowMenu
        topicId={topicId}
        deleting={deleting}
        onEdit={handleEnterUpdate}
        onRequestDelete={() => setShowDeleteSheet(true)}
      />
    );
  };

  return (
    <IonPage>
      <IonHeader>{renderToolbar()}</IonHeader>
      <IonContent style={pageBackground}>
        <PageRefresher />
        {!isCreateMode &&
        (loading ||
          (!topic && !invitePreviewData && !privateInviteResolved)) ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <IonSpinner name="crescent" />
          </div>
        ) : initialMode !== 'create' && !topic && invitePreviewData ? (
          <PrivateInvitePlaceholder
            preview={invitePreviewData}
            onLinkPhone={goToProfileWizardForInvite}
          />
        ) : initialMode !== 'create' && !topic ? (
          <NotFoundCard />
        ) : (
          <>
            {isViewMode && topic ? (
              <div style={readHeroStyle}>
                <div style={heroRowStyle}>
                  {topic.imageData && (
                    <div style={heroImageColStyle}>
                      <img
                        src={topic.imageData}
                        alt=""
                        data-testid="topic-image-preview"
                        style={heroImageStyle}
                      />
                    </div>
                  )}
                  <div style={heroInfoColStyle}>
                    <div style={heroTitleRowStyle}>
                      <h1 style={heroTitleStyle}>{pageHeader}</h1>
                      {renderTopicsMenu()}
                    </div>
                    {renderTypeSection()}
                  </div>
                </div>
              </div>
            ) : (
              <div style={centeredHeaderStyle}>
                <div style={pageHeaderRowStyle}>
                  <h1 style={pageHeaderTitleStyle}>{pageHeader}</h1>
                </div>
              </div>
            )}

            <div
              style={{
                padding: '8px 16px',
                paddingBottom: isViewMode && topic ? 120 : 20,
              }}
            >
              {renderBasicsSection()}
              {renderBodySection()}
              {renderDateTimeSection()}
              {renderEngagementSection()}
              {renderResharingLimitsSection()}
              {renderViewTopicsSection()}
              {renderChangesLinkSection()}
              {renderCustomizeTemplatesLink()}
              {renderCreateFooter()}
              {renderListingManagersSection()}
            </div>
          </>
        )}

        {topic && (
          <DeleteTopicSheet
            topicTitle={topic.title}
            isOpen={showDeleteSheet}
            onDismiss={() => setShowDeleteSheet(false)}
            onDelete={handleDeleteTopic}
          />
        )}
      </IonContent>

      {renderShareDock()}

      <CustomizeTemplatesModal
        isOpen={showTemplatesModal}
        topicType={type}
        value={customTemplates}
        onDone={handleDoneTemplates}
        onRevert={handleRevertTemplates}
        onClose={() => setShowTemplatesModal(false)}
      />

      {interstitials}
    </IonPage>
  );
};

export default TopicView;
